import { Builder } from '@builder.io/react';
import dynamic from 'next/dynamic';
import { Icon } from '#const';
import { getIconUrl } from '#utils';

import { defaultReviewTextPlaceholder, defaultTestimonialItems } from './defaults';

const TestimonialCarousel = dynamic(() => import('./TestimonialCarousel'));

export const defaultOptions = {
	sectionTitle: '<h2>Testimonials</h2>',
	sectionSubtitle: '<p>See what our clients have to say about their experience.</p>',
	sectionPadding: 48,
	usePageColors: true,
	headlineColor: '#0f766e',
	accentNavColor: '#115e59',
	titleTextColor: '#1e293b',
	sectionBackgroundColor: '#f8fafc',
	cardBackgroundColor: '#ffffff',
} as const;

Builder.registerComponent(TestimonialCarousel, {
	name: 'TestimonialCarousel',
	friendlyName: 'Testimonial Carousel',
	inputs: [
		{
			name: 'sectionTitle',
			friendlyName: 'Section title',
			type: 'richText',
			defaultValue: defaultOptions.sectionTitle,
			helperText: 'Font size is fixed and cannot be changed.',
		},
		{
			name: 'sectionSubtitle',
			friendlyName: 'Section subtitle',
			type: 'richText',
			defaultValue: defaultOptions.sectionSubtitle,
			helperText: 'Font size is fixed and cannot be changed.',
		},
		{
			name: 'sectionPadding',
			friendlyName: 'Section padding (px)',
			type: 'number',
			defaultValue: defaultOptions.sectionPadding,
			helperText: 'Top and bottom padding of the section in pixels.',
		},
		{
			name: 'items',
			friendlyName: 'Testimonials',
			type: 'list',
			defaultValue: [...defaultTestimonialItems],
			subFields: [
				{
					name: 'reviewerName',
					friendlyName: 'Reviewer name',
					type: 'string',
					defaultValue: 'Client name',
				},
				{
					name: 'reviewTitle',
					friendlyName: 'Review title',
					type: 'string',
					defaultValue: 'Short summary of their experience',
				},
				{
					name: 'reviewText',
					friendlyName: 'Review text',
					type: 'longText',
					defaultValue: defaultReviewTextPlaceholder,
				},
			],
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			onChange: function (options: any) {
				const list = options.get('items');
				if (!list) {
					return;
				}
				if (list.length > 12) {
					options.set('items', list.slice(0, 12));
					alert('Maximum 12 testimonials — remove items to add more.');
				}
			},
		},
		{
			name: 'usePageColors',
			friendlyName: 'Use page colors',
			type: 'boolean',
			defaultValue: defaultOptions.usePageColors,
			helperText:
				'When on, review title uses secondary accent and arrows use primary accent from the page. When off, use the color fields below.',
		},
		{
			name: 'headlineColor',
			friendlyName: 'Review title color',
			type: 'color',
			defaultValue: defaultOptions.headlineColor,
			showIf: function (options) {
				return options.get('usePageColors') === false;
			},
		},
		{
			name: 'accentNavColor',
			friendlyName: 'Arrows & active dot',
			type: 'color',
			defaultValue: defaultOptions.accentNavColor,
			showIf: function (options) {
				return options.get('usePageColors') === false;
			},
		},
		{
			name: 'titleTextColor',
			friendlyName: 'Title & reviewer name',
			type: 'color',
			defaultValue: defaultOptions.titleTextColor,
			showIf: function (options) {
				return options.get('usePageColors') === false;
			},
		},
		{
			name: 'sectionBackgroundColor',
			friendlyName: 'Section background',
			type: 'color',
			defaultValue: defaultOptions.sectionBackgroundColor,
		},
		{
			name: 'cardBackgroundColor',
			friendlyName: 'Card background',
			type: 'color',
			defaultValue: defaultOptions.cardBackgroundColor,
		},
	],
	image: getIconUrl(Icon.COLUMNS),
});
